import { memo, useState, useCallback, useMemo } from 'react';
import { Handle, type NodeProps, type Node } from '@xyflow/react';
import {
  makeStyles,
  tokens,
  mergeClasses,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Button,
  Tooltip,
  Input,
} from '@coreai/fluentui-react';
import { NavigationFilled, ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons';
import { useHandleLayout } from './useHandlePositions';
import { ExecutionOverlay } from '@/components/shared';
import {
  FloatingNode,
  NodeHoverToolbar,
  useDetailLevel,
  type NodeStatus,
} from './FloatingNode';
import { getBuiltInManifest } from '@/data/manifests';
import type { NodeExecutionStatus } from '@/types/monitoring';
import { useWorkflowStore } from '@/stores/workflowStore';
import { isSwitchAction } from '@/types/workflowDefinition';

export type FloatingSwitchNodeData = {
  label: string;
  actionType: string;
  actionName: string;
  cases?: string[];
  // Monitoring mode props
  executionStatus?: NodeExecutionStatus;
  executionDuration?: number;
  /** Which case was taken during execution */
  caseTaken?: string;
  // Toolbar action callbacks (injected by WorkflowDesigner)
  onOpen?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onEditWithAI?: () => void;
};

export type FloatingSwitchNodeType = Node<FloatingSwitchNodeData, 'floatingSwitch'>;

// Max cases to show inline before collapsing (2 visible + expand button)
const MAX_VISIBLE_CASES = 2;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  // Container for case outputs
  casesContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalMNudge,
    marginTop: tokens.spacingVerticalS,
    flexWrap: 'wrap',
    maxWidth: '240px',
  },
  // Individual case wrapper
  caseWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    position: 'relative',
  },
  // Case label styling
  caseLabel: {
    fontSize: '9px', // Sub-scale intentional — fits tight case labels
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteBlueForeground2,
    maxWidth: '60px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '1',
  },
  defaultLabel: {
    color: tokens.colorNeutralForeground3,
  },
  caseEditInput: {
    maxWidth: '70px',
    '& input': {
      fontSize: '9px', // Sub-scale intentional
      paddingBlock: 0,
      paddingInline: tokens.spacingHorizontalXXS,
    },
  },
  // Handle styling with expanded hit area for accessibility
  handleBase: {
    width: '14px !important',
    height: '14px !important',
    borderRadius: '50%',
    position: 'relative',
    transform: 'none',
    top: '0',
    left: '0',
    cursor: 'crosshair',
    // Expand hit area to 44px for touch accessibility
    '::before': {
      content: '""',
      position: 'absolute',
      width: '44px',
      height: '44px',
      left: '-15px',
      top: '-15px',
      borderRadius: '50%',
    },
  },
  caseHandle: {
    backgroundColor: `${tokens.colorPaletteBlueBackground2} !important`,
    border: `2px solid ${tokens.colorNeutralStroke2} !important`,
  },
  defaultHandle: {
    backgroundColor: `${tokens.colorNeutralBackground3} !important`,
    border: `2px solid ${tokens.colorNeutralStroke2} !important`,
  },
  // Case taken indicator
  caseTaken: {
    fontWeight: tokens.fontWeightBold,
    textDecoration: 'underline',
  },
  caseNotTaken: {
    opacity: 0.4,
  },
  // Expand button wrapper (contains button + hidden handles)
  expandWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    position: 'relative',
  },
  // Expand button
  expandButton: {
    fontSize: '9px', // Sub-scale intentional
    color: tokens.colorNeutralForeground3,
    paddingBlock: tokens.spacingVerticalXXS,
    paddingInline: tokens.spacingHorizontalSNudge,
    minWidth: 'auto',
    height: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  expandIcon: {
    fontSize: tokens.fontSizeBase200,
  },
  // Hidden handles container (rendered in DOM but visually grouped)
  hiddenHandlesRow: {
    display: 'flex',
    gap: tokens.spacingVerticalSNudge,
    marginTop: tokens.spacingVerticalXS,
  },
  // Expanded popover (shows labels only, handles are in DOM)
  expandedPopover: {
    padding: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalSNudge,
    minWidth: '100px',
  },
  expandedCaseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'default',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
  },
  expandedCaseIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorPaletteBlueBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  expandedCaseLabel: {
    fontSize: '11px', // No exact Fluent token for 11px — intentional half-step
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Compact floating-style Switch Node with collapsible case outputs.
 * Shows up to 2 cases inline, collapses to "+N more" if more cases exist.
 * All handles are rendered in the node DOM for proper ReactFlow connectivity.
 */
export const FloatingSwitchNode = memo(
  ({ data, selected }: NodeProps<FloatingSwitchNodeType>) => {
    const styles = useStyles();
    const handles = useHandleLayout();
    const detailLevel = useDetailLevel();
    const [isExpanded, setIsExpanded] = useState(false);

    const builtInManifest = useMemo(() => getBuiltInManifest(data.actionType), [data.actionType]);
    const brandColor = builtInManifest?.properties?.brandColor as string | undefined;

    const cases = useMemo(() => data.cases || [], [data.cases]);
    const hasManyCases = cases.length > MAX_VISIBLE_CASES;
    const visibleCases = useMemo(
      () => (hasManyCases ? cases.slice(0, MAX_VISIBLE_CASES) : cases),
      [cases, hasManyCases]
    );
    const hiddenCases = useMemo(
      () => (hasManyCases ? cases.slice(MAX_VISIBLE_CASES) : []),
      [cases, hasManyCases]
    );
    const hiddenCount = hiddenCases.length;
    const hasCaseInfo = data.caseTaken !== undefined;

    // Map execution status to FloatingNode status
    const getNodeStatus = (): NodeStatus => {
      if (!data.executionStatus) return 'default';

      switch (data.executionStatus) {
        case 'running':
          return 'running';
        case 'succeeded':
          return 'success';
        case 'failed':
          return 'failed';
        case 'skipped':
          return 'skipped';
        default:
          return 'default';
      }
    };

    // Use functional update to avoid stale closure
    const handleExpandClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setIsExpanded((prev) => !prev);
    }, []);

    // Keyboard navigation for popover
    const handlePopoverKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsExpanded(false);
      }
    }, []);

    const isMonitoringMode = data.executionStatus !== undefined;

    // Inline case value editing
    const [editingCase, setEditingCase] = useState<string | null>(null);
    const [editValue, setEditValue] = useState('');
    const updateSwitchCaseValue = useWorkflowStore((s) => s.updateSwitchCaseValue);

    const handleCaseDoubleClick = useCallback((caseName: string) => {
      if (isMonitoringMode) return;
      setEditingCase(caseName);
      // Initialize with the actual case match value, not the case key
      const switchAction = useWorkflowStore.getState().workflow?.definition.actions[data.actionName];
      const caseValue = switchAction && isSwitchAction(switchAction)
        ? switchAction.cases?.[caseName]?.case
        : undefined;
      setEditValue(caseValue != null ? String(caseValue) : caseName);
    }, [isMonitoringMode, data.actionName]);

    const commitCaseEdit = useCallback(() => {
      if (editingCase && editValue.trim()) {
        updateSwitchCaseValue(data.actionName, editingCase, editValue.trim());
      }
      setEditingCase(null);
    }, [editingCase, editValue, data.actionName, updateSwitchCaseValue]);

    const handleCaseEditKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        commitCaseEdit();
      } else if (e.key === 'Escape') {
        setEditingCase(null);
      }
    }, [commitCaseEdit]);

    // Render a single case output with handle
    const renderCaseOutput = (caseName: string, isDefault = false) => {
      const isTaken = hasCaseInfo && data.caseTaken === caseName;
      const isNotTaken = hasCaseInfo && data.caseTaken !== caseName;
      const isEditing = editingCase === caseName && !isDefault;

      return (
        <div key={caseName} className={styles.caseWrapper}>
          {isEditing ? (
            <Input
              autoFocus
              size="small"
              value={editValue}
              onChange={(_e, d) => setEditValue(d.value)}
              onBlur={commitCaseEdit}
              onKeyDown={handleCaseEditKeyDown}
              className={styles.caseEditInput}
              aria-label={`Edit case value for ${caseName}`}
            />
          ) : (
          <Tooltip content={caseName} relationship="label" positioning="below">
            <span
              className={mergeClasses(
                styles.caseLabel,
                isDefault && styles.defaultLabel,
                isTaken && styles.caseTaken,
                isNotTaken && styles.caseNotTaken
              )}
              onDoubleClick={() => !isDefault && handleCaseDoubleClick(caseName)}
              role={isDefault ? undefined : 'button'}
              tabIndex={isDefault ? undefined : 0}
              onKeyDown={(e) => {
                if (!isDefault && (e.key === 'Enter' || e.key === 'F2')) {
                  handleCaseDoubleClick(caseName);
                }
              }}
            >
              {isDefault ? 'default' : caseName}
            </span>
          </Tooltip>
          )}
          <Handle
            type="source"
            position={handles.output.position}
            id={isDefault ? 'default' : `case-${caseName}`}
            isConnectable={!isMonitoringMode}
            className={mergeClasses(
              styles.handleBase,
              isDefault ? styles.defaultHandle : styles.caseHandle
            )}
            aria-label={`${isDefault ? 'Default' : caseName} case output`}
          />
        </div>
      );
    };

    return (
      <div className={styles.wrapper}>
        <NodeHoverToolbar
          onConfigure={data.onOpen}
          onDelete={data.onDelete}
          onDuplicate={data.onDuplicate}
          onEditWithAI={data.onEditWithAI}
          showToolbar={!isMonitoringMode}
          disabled={selected}
        >
          <FloatingNode
            icon={<NavigationFilled aria-hidden="true" />}
            label={data.label}
            subLabel="Switch"
            detailLevel={detailLevel}
            selected={selected}
            status={getNodeStatus()}
            accentColor="purple"
            brandColorOverride={brandColor}
            shape="diamond"
            ariaLabel={`${data.label} switch node with ${cases.length} cases`}
          >
            <Handle
              type="target"
              position={handles.input.position}
              id="input"
              isConnectable={!isMonitoringMode}
              style={handles.input.style}
              aria-label="Switch input"
            />
          </FloatingNode>
        </NodeHoverToolbar>

        {/* Case outputs - ALL handles rendered in DOM for ReactFlow connectivity */}
        <div className={styles.casesContainer}>
          {/* Visible cases */}
          {visibleCases.map((caseName) => renderCaseOutput(caseName))}

          {/* Hidden cases section */}
          {hasManyCases && (
            <div className={styles.expandWrapper}>
              {/* Expand/collapse button with popover for labels */}
              <Popover
                open={isExpanded}
                onOpenChange={(_, popoverData) => setIsExpanded(popoverData.open)}
                positioning="below"
              >
                <PopoverTrigger disableButtonEnhancement>
                  <Button
                    className={styles.expandButton}
                    appearance="subtle"
                    size="small"
                    onClick={handleExpandClick}
                    aria-expanded={isExpanded}
                    aria-label={`Show ${hiddenCount} more cases`}
                  >
                    +{hiddenCount}
                    {isExpanded ? (
                      <ChevronUpRegular className={styles.expandIcon} />
                    ) : (
                      <ChevronDownRegular className={styles.expandIcon} />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverSurface
                  className={styles.expandedPopover}
                  role="menu"
                  aria-label="Additional switch cases"
                >
                  {hiddenCases.map((caseName) => {
                    const isTaken = hasCaseInfo && data.caseTaken === caseName;
                    return (
                      <div
                        key={caseName}
                        className={styles.expandedCaseRow}
                        role="menuitem"
                        tabIndex={0}
                        onKeyDown={handlePopoverKeyDown}
                      >
                        <div className={styles.expandedCaseIndicator} />
                        <span
                          className={mergeClasses(
                            styles.expandedCaseLabel,
                            isTaken && styles.caseTaken
                          )}
                        >
                          {caseName}
                        </span>
                      </div>
                    );
                  })}
                </PopoverSurface>
              </Popover>

              {/* Hidden case handles - rendered in DOM but shown below expand button */}
              <div className={styles.hiddenHandlesRow}>
                {hiddenCases.map((caseName) => {
                  const isTaken = hasCaseInfo && data.caseTaken === caseName;
                  const isNotTaken = hasCaseInfo && data.caseTaken !== caseName;
                  return (
                    <Tooltip key={caseName} content={caseName} relationship="label">
                      <Handle
                        type="source"
                        position={handles.output.position}
                        id={`case-${caseName}`}
                        isConnectable={!isMonitoringMode}
                        className={mergeClasses(
                          styles.handleBase,
                          styles.caseHandle,
                          isNotTaken && styles.caseNotTaken
                        )}
                        aria-label={`${caseName} case output`}
                        style={isTaken ? { opacity: 1 } : undefined}
                      />
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          )}

          {/* Default case - always visible */}
          {renderCaseOutput('default', true)}
        </div>

        {/* Execution overlay */}
        {isMonitoringMode && (
          <ExecutionOverlay
            status={data.executionStatus!}
            duration={data.executionDuration}
            position="top-right"
          />
        )}
      </div>
    );
  }
);

FloatingSwitchNode.displayName = 'FloatingSwitchNode';
